/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'react-moment';
import { withTranslation } from 'react-i18next';
import ContentLoader from 'react-content-loader';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { formatRoute } from 'react-router-named-routes';
// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
// global variables
import { adStatus, productionUrl } from 'config';
// material-ui icons
import Edit from '@material-ui/icons/Edit';
import ArrowForward from '@material-ui/icons/ArrowForward';
import Person from '@material-ui/icons/PersonOutlined';
import DateRange from '@material-ui/icons/DateRange';
import FilterList from '@material-ui/icons/FilterList';
import Visibility from '@material-ui/icons/VisibilityOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import ErrorIcon from '@material-ui/icons/Error';
import ArchiveIcon from '@material-ui/icons/Archive';
// our components
import getCleanHTML from 'helpers/cleanHtmlHelper';
import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles';
import CardBody from 'components/UI/Card/CardBody';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import Card from 'components/UI/Card/Card';
import WithPermission from 'components/User/WithPermission';
import { AD, PAID_AD } from 'routes/paths';
import defaultImage from 'assets/img/default_image.jpg';

/**
 * @class
 * @extends React.Component
 */
class AdInfo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPhotoIndex: -1,
      showImageViewer: false,
    };
  }

  /**
   * @description
   * Return the right icon for an image status
   *
   * @param {Object} image - Image object.
   * @param {*} t - Prop from HOC with string translations.
   * @returns {*} Images icon.
   */
  imageIcon = (image, t) => {
    let icon;
    const status = image.lastStatus || image.status;
    switch (status) {
      default:
        icon = (
          <CheckCircleOutlineIcon
            titleAccess={t(`ad.details.images.${status}`)}
            color="primary"
          />
        );
        break;
      case 'READY':
        icon = (
          <CheckCircleIcon
            titleAccess={t(`ad.details.images.${status}`)}
            color="primary"
          />
        );
        break;
      case 'ERROR':
        icon = (
          <ErrorIcon
            titleAccess={t(`ad.details.images.${status}`)}
            color="error"
          />
        );
        break;
      case 'REMOVED':
        icon = (
          <DeleteForeverIcon
            titleAccess={t(`ad.details.images.${status}`)}
            color="error"
          />
        );
        break;
      case 'EXPIRED':
        icon = (
          <ArchiveIcon
            titleAccess={t(`ad.details.images.${status}`)}
            color="disabled"
          />
        );
        break;
    }
    return icon;
  };

  /**
   * @description
   * Render every image related to ad.
   *
   * @param {Object} image - Image object.
   * @param {number} key - Image position.
   * @param {Object} classes - Prop styles received from withStyles.
   * @param {*} t - Prop from HOC with string translations.
   * @returns {*} Images.
   */
  renderImage = (image, key, classes, t) => {
    const icon = this.imageIcon(image, t);

    return (
      <div className={classes.imageIconContainer} key={`image-${key}`}>
        <div className={classes.imageIcon}>{icon}</div>
        <a
          href={image.urls.high}
          target="_blank"
          rel="noopener noreferrer"
          data-cy="zoomAdImage"
          onClick={event => {
            event.preventDefault();
            this.setState({ currentPhotoIndex: key, showImageViewer: true });
          }}
        >
          <img
            src={image.urls.thumb || defaultImage}
            alt="foto"
            onError={e => {
              e.target.onerror = null;
              e.target.src = defaultImage;
            }}
          />
        </a>
      </div>
    );
  };

  /**
   * @description
   * Render Ad Info.
   */
  render() {
    const { classes, data, loading, link, t } = this.props;
    const { currentPhotoIndex, showImageViewer } = this.state;
    const images =
      Object.keys(data).length > 0 && data.ad && data.ad.images
        ? data.ad.images.map(image => image.urls.high)
        : [];
    return (
      <Card>
        {!loading ? (
          <CardBody>
            <GridContainer>
              <GridItem>
                <div className={classes.adStatus}>
                  {(data.ad.status === adStatus.PUBLISHED_BY_PROCESSOR ||
                    data.ad.status === adStatus.PUBLISHED_BY_MODERATOR) && (
                    <p
                      data-cy="adStatus"
                      className={`${classes.infoAdPublish}
                              ${classes.verticalAlignment}`}
                    >
                      {t(`ad.details.${data.ad.status}`)}
                    </p>
                  )}
                  {(data.ad.status === adStatus.UNPUBLISHED_BY_MODERATOR ||
                    data.ad.status === adStatus.UNPUBLISHED_BY_PROCESSOR ||
                    data.ad.status === adStatus.UNPUBLISHED_BY_USER ||
                    data.ad.status === adStatus.UNPUBLISHED_BY_USER_DELETED ||
                    data.ad.status ===
                      adStatus.UNPUBLISHED_BY_USER_BLOCKED) && (
                    <p
                      data-cy="adStatus"
                      className={`${classes.infoAdPublish}
                             ${classes.infoAdUnPublish}
                             ${classes.verticalAlignment}`}
                    >
                      {t(`ad.details.${data.ad.status}`)}
                    </p>
                  )}
                  {(data.ad.status === adStatus.PENDING_VERIFICATION ||
                    data.ad.status === adStatus.PENDING_PROCESSING ||
                    data.ad.status === adStatus.TO_REVIEW ||
                    data.ad.status === adStatus.PROCESSING) && (
                    <p
                      data-cy="adStatus"
                      className={`${classes.infoAdPublish}
                             ${classes.infoAdPending}
                             ${classes.verticalAlignment}`}
                    >
                      {t(`ad.details.${data.ad.status}`)}
                    </p>
                  )}
                  {data.ad.status === adStatus.EXPIRED && (
                    <p
                      data-cy="adStatus"
                      className={`${classes.infoAdPublish}
                            ${classes.infoAdExpired}
                            ${classes.verticalAlignment}`}
                    >
                      {t(`ad.details.${data.ad.status}`)}
                    </p>
                  )}
                </div>
              </GridItem>
              <GridItem>
                <p
                  data-cy="adCategorySubcategory"
                  className={`${classes.adCategories}
                        ${classes.verticalAlignment}`}
                >
                  {data.ad.subcategory.parentCategory.title}
                  <span
                    className={classes.infoAdValues}
                    style={{ lineHeight: 0 }}
                  >
                    <ArrowForward color="disabled" />
                  </span>
                  {data.ad.subcategory.title}
                </p>
              </GridItem>
            </GridContainer>
            <GridContainer alignItems="center">
              <GridItem>
                <h5>
                  <span data-cy="adId" className={classes.infoAdId}>
                    ID:{' '}
                    <span className={classes.infoAdValues}> {data.ad.id} </span>
                  </span>
                  {data.ad.autoPaidad && (
                    <span data-cy="adId" className={classes.infoAdId}>
                      AUTO ID:{' '}
                      <WithPermission
                        allowPermissions={[
                          'view_autopaidad',
                          'view_ownership_autopaidad',
                        ]}
                      >
                        {data.ad.autoPaidad && (
                          <a
                            rel="noopener noreferrer"
                            target="_blank"
                            href={formatRoute(PAID_AD.DETAILS, {
                              id: data.ad.autoPaidad.id,
                            })}
                            className={classes.adLinks}
                          >
                            {data.ad.autoPaidad.id}
                          </a>
                        )}
                      </WithPermission>
                    </span>
                  )}
                </h5>
              </GridItem>
              <GridItem>
                <a
                  className={classes.anchorButton}
                  href={`${AD.LIST}?sameOwnerAds=${data.ad.id}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('common.related')}
                </a>
              </GridItem>
            </GridContainer>
            <GridContainer>
              <GridItem xs={12}>
                {data.ad.isAuto && (
                  <WithPermission
                    allowPermissions={[
                      'view_autopaidad',
                      'view_ownership_autopaidad',
                    ]}
                  >
                    <small className={classes.adAuto}>AUTO</small>
                  </WithPermission>
                )}
                {data.ad.isPromoted && (
                  <WithPermission
                    allowPermissions={[
                      'view_promotedad',
                      'view_ownership_promotedad',
                    ]}
                  >
                    <small className={classes.adAuto}>DESTACADO</small>
                  </WithPermission>
                )}
                <span
                  className={classes.adTitle}
                  data-cy="adTitle"
                  id={`ad-${data.ad.id}`}
                >
                  {data.ad.title}
                </span>
              </GridItem>
            </GridContainer>
            <GridContainer style={{ marginTop: '20px' }}>
              <GridItem xs={12}>
                <div className={classes.secondaryInfoContainer}>
                  <div>
                    <div data-cy="adPrice" className={classes.secondaryAdInfo}>
                      {t('ad.details.price')}
                      {data.ad.price ? (
                        <span className={classes.infoAdValues}>
                          {data.ad.price} {data.ad.currency}
                        </span>
                      ) : (
                        <span style={{ fontWeight: 500, fontStyle: 'italic' }}>
                          -
                        </span>
                      )}
                    </div>
                    <div
                      data-cy="adLocation"
                      className={classes.secondaryAdInfo}
                    >
                      {t('ad.details.location')}
                      <span className={classes.infoAdValues}>
                        {data.ad.municipality
                          ? `${data.ad.province.name}, 
                          ${data.ad.municipality.name}`
                          : data.ad.province.name}
                      </span>
                    </div>
                    <div
                      data-cy="adIpCreated"
                      className={`${classes.secondaryAdInfo}
                      ${classes.secondaryInfoBlocks}`}
                    >
                      {t('ad.details.country_ip')}
                      <div
                        className={classes.infoAdValues}
                        style={{ marginLeft: 4 }}
                      >
                        {data.ad.countryInsert}{' '}
                        {data.ad.ipInsert === null
                          ? t('common.unknown')
                          : data.ad.ipInsert}
                      </div>
                      {data.ad.updateCount > 0 && (
                        <div
                          data-cy="adIpUpdated"
                          className={classes.infoAdValues}
                          style={{
                            marginLeft: '15px',
                          }}
                        >
                          <Edit />
                          {data.ad.countryUpdate}{' '}
                          {data.ad.ipUpdate === null
                            ? t('common.unknown')
                            : data.ad.ipUpdate}
                        </div>
                      )}
                    </div>
                    <div
                      data-cy="adBotScore"
                      className={`${classes.secondaryAdInfo}
                      ${classes.secondaryInfoBlocks}`}
                    >
                      {t('ad.details.score')}
                      <div
                        className={classes.infoAdValues}
                        style={{ marginLeft: 4 }}
                      >
                        {data.ad.botScoreInsert === null
                          ? t('common.unknown')
                          : data.ad.botScoreInsert}
                      </div>
                      {data.ad.botScoreInsertErrors && (
                        <div className={classes.botScoreError}>
                          {data.ad.botScoreInsertErrors}
                        </div>
                      )}
                      {data.ad.updateCount > 0 && (
                        <React.Fragment>
                          <div
                            className={classes.infoAdValues}
                            style={{ marginLeft: '15px' }}
                          >
                            <Edit />
                            {data.ad.botScoreUpdate === null
                              ? t('common.unknown')
                              : data.ad.botScoreUpdate}
                          </div>
                          {data.ad.botScoreUpdateErrors && (
                            <div className={classes.botScoreError}>
                              {data.ad.botScoreUpdateErrors}
                            </div>
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                  <div>
                    <div data-cy="adUpdatedOn" className={classes.adDate}>
                      {data.ad.updatedOnByUser && (
                        <div className={classes.dateAlignment}>
                          <Person className={classes.dateIconSize} />{' '}
                          {data.ad.updateCount} •
                          <span style={{ marginLeft: 4 }}>
                            <Moment
                              date={data.ad.updatedOnByUser}
                              format="D MMM Y, HH:mm"
                            />
                          </span>
                        </div>
                      )}
                    </div>
                    <div data-cy="adUpdatedOn" className={classes.adDate}>
                      {data.ad.updatedOn && (
                        <div className={classes.dateAlignment}>
                          <DateRange className={classes.dateIconSize} />
                          <Moment
                            date={data.ad.updatedOn}
                            format="D MMM Y, HH:mm"
                          />
                        </div>
                      )}
                    </div>
                    <div
                      data-cy="adUpdatedOnToOrder"
                      className={classes.adDate}
                    >
                      {data.ad.updatedOnToOrder && (
                        <div className={classes.dateAlignment}>
                          <FilterList className={classes.dateIconSize} />
                          <Moment
                            date={data.ad.updatedOnToOrder}
                            format="D MMM Y, HH:mm"
                          />
                        </div>
                      )}
                    </div>
                    <div data-cy="adViewCount" className={classes.adDate}>
                      {data.ad.viewCount && (
                        <div className={classes.dateAlignment}>
                          <Visibility className={classes.dateIconSize} />{' '}
                          {data.ad.viewCount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </GridItem>
            </GridContainer>
            {data.ad.description && (
              <div
                data-cy="adDescription"
                className={classes.adDescription}
                dangerouslySetInnerHTML={{
                  __html: getCleanHTML(data.ad.description),
                }}
              />
            )}
            <GridItem>
              <div className={classes.imagesIconsContainer}>
                {data.ad.images &&
                  data.ad.images.map((image, key) =>
                    this.renderImage(image, key, classes, t),
                  )}
              </div>
            </GridItem>
            <GridItem>
              {data.ad && data.ad.status !== adStatus.EXPIRED && (
                <div className={classes.userDetails}>
                  {link || (
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href={productionUrl + data.ad.permalink}
                      className={classes.adLinks}
                    >
                      {productionUrl + data.ad.permalink}
                    </a>
                  )}
                </div>
              )}
            </GridItem>

            {showImageViewer && (
              <Lightbox
                mainSrc={images[currentPhotoIndex]}
                nextSrc={images[(currentPhotoIndex + 1) % images.length]}
                prevSrc={
                  images[
                    (currentPhotoIndex + images.length - 1) % images.length
                  ]
                }
                onCloseRequest={() => this.setState({ showImageViewer: false })}
                onMovePrevRequest={() =>
                  this.setState({
                    currentPhotoIndex:
                      (currentPhotoIndex + images.length - 1) % images.length,
                  })
                }
                onMoveNextRequest={() =>
                  this.setState({
                    currentPhotoIndex: (currentPhotoIndex + 1) % images.length,
                  })
                }
              />
            )}
          </CardBody>
        ) : (
          <ContentLoader
            height={871}
            width={1073}
            speed={2}
            primaryColor="#d9d9d9"
            secondaryColor="#ecebeb"
          >
            <path
              // eslint-disable-next-line max-len
              d="M764,61 L1065,61 C1069.41828,61 1073,64.581722 1073,69 L1073,121 C1073,125.418278 1069.41828,129 1065,129 L764,129 C759.581722,129 756,125.418278 756,121 L756,69 C756,64.581722 759.581722,61 764,61 Z M764,153 L1065,153 C1069.41828,153 1073,156.581722 1073,161 L1073,661 C1073,665.418278 1069.41828,669 1065,669 L764,669 C759.581722,669 756,665.418278 756,661 L756,161 C756,156.581722 759.581722,153 764,153 Z M764,685 L1065,685 C1069.41828,685 1073,688.581722 1073,693 L1073,863 C1073,867.418278 1069.41828,871 1065,871 L764,871 C759.581722,871 756,867.418278 756,863 L756,693 C756,688.581722 759.581722,685 764,685 Z M100,61 L701,61 C705.418278,61 709,64.581722 709,69 L709,176 C709,180.418278 705.418278,184 701,184 L100,184 C95.581722,184 92,180.418278 92,176 L92,69 C92,64.581722 95.581722,61 100,61 Z M100,213 L701,213 C705.418278,213 709,216.581722 709,221 L709,328 C709,332.418278 705.418278,336 701,336 L100,336 C95.581722,336 92,332.418278 92,328 L92,221 C92,216.581722 95.581722,213 100,213 Z M100,365 L701,365 C705.418278,365 709,368.581722 709,373 L709,480 C709,484.418278 705.418278,488 701,488 L100,488 C95.581722,488 92,484.418278 92,480 L92,373 C92,368.581722 95.581722,365 100,365 Z M32.5,61 C50.4492544,61 65,75.5507456 65,93.5 C65,111.449254 50.4492544,126 32.5,126 C14.5507456,126 2.19814969e-15,111.449254 0,93.5 C-2.19814969e-15,75.5507456 14.5507456,61 32.5,61 Z M32.5,213 C50.4492544,213 65,227.550746 65,245.5 C65,263.449254 50.4492544,278 32.5,278 C14.5507456,278 2.19814969e-15,263.449254 0,245.5 C-2.19814969e-15,227.550746 14.5507456,213 32.5,213 Z M32.5,365 C50.4492544,365 65,379.550746 65,397.5 C65,415.449254 50.4492544,430 32.5,430 C14.5507456,430 2.19814969e-15,415.449254 0,397.5 C-2.19814969e-15,379.550746 14.5507456,365 32.5,365 Z M19.5,0 L163.5,0 C174.269553,-1.97833472e-15 183,8.73044738 183,19.5 C183,30.2695526 174.269553,39 163.5,39 L19.5,39 C8.73044738,39 1.31888981e-15,30.2695526 0,19.5 C-1.31888981e-15,8.73044738 8.73044738,1.97833472e-15 19.5,0 Z M225.5,0 L345.5,0 C356.269553,-1.97833472e-15 365,8.73044738 365,19.5 C365,30.2695526 356.269553,39 345.5,39 L225.5,39 C214.730447,39 206,30.2695526 206,19.5 C206,8.73044738 214.730447,1.97833472e-15 225.5,0 Z"
              fill="#E5EAEC"
            />
          </ContentLoader>
        )}
      </Card>
    );
  }
}

/**
 * AdInfo PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop user info received from parents
 * @param loading - prop to show loading state
 * @param link - prop to show link to ad
 * @param t - prop from HOC with string translations
 */
AdInfo.propTypes = {
  data: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.shape({})])
    .isRequired,
  classes: PropTypes.instanceOf(Object).isRequired,
  loading: PropTypes.bool,
  link: PropTypes.element,
  t: PropTypes.func.isRequired,
};

AdInfo.defaultProps = {
  link: null,
  loading: false,
};

export default withTranslation()(withStyles(userProfileStyles)(AdInfo));
